import { ALL_CARD_FIELDS, type CardFields, type MoveFn } from "@/components/dashboard/candidate-list";
import { StatusPill } from "@/components/dashboard/status-pill";
import { STALL_DAYS, type SalaryTone, formatMoney, salaryTone } from "@/lib/format";
import { cn } from "@/lib/utils";
import type { CandidateCard, StageDef } from "@ow/shared";
import { Plus } from "lucide-react";
import { type DragEvent, useCallback, useEffect, useRef, useState } from "react";

const TONE_TEXT: Record<NonNullable<SalaryTone>, string> = {
  success: "text-[var(--color-success)]",
  warning: "text-[var(--color-warning)]",
  destructive: "text-[var(--color-destructive)]",
};
const TONE_BG: Record<NonNullable<SalaryTone>, string> = {
  success: "bg-[var(--color-success)]",
  warning: "bg-[var(--color-warning)]",
  destructive: "bg-[var(--color-destructive)]",
};

// A candidate in a terminal state (rejected / withdrawn / placed) can't be dragged to another stage — the
// board mirrors the drawer, where advancing or moving a closed candidate is blocked. To move one, reopen it
// from the candidate drawer first.
const TERMINAL_STATUSES = new Set<string>(["rejected", "withdrawn", "placed"]);

/** Compact card for a board column. The column header already names the stage, so the card carries only
 *  what identifies and qualifies the person, in a clean two-tier hierarchy: an identity block (name over
 *  current role · company) and a quiet meta footer (status pill + a bare "19d" — the column supplies the
 *  "in stage"). No leading avatar (the columns are narrow) and no colour spine (the status pill already
 *  colour-codes the card). A stalled candidate (>14 days) turns its day-count amber — colour alone, no
 *  extra dot. Salary (opt-in, not shown in the client view) drops to the foot when enabled. */
function KanbanCard({
  c,
  searchId,
  budgetMin,
  budgetMax,
  budgetCurrency,
  fields,
  onOpenCandidate,
  editable,
  isDragging,
  onDragStart,
  onDragEnd,
}: {
  c: CandidateCard;
  searchId: string;
  budgetMin: number | null;
  budgetMax: number | null;
  budgetCurrency: string;
  fields: CardFields;
  onOpenCandidate?: (candidateId: string) => void;
  // Editable board (admin edit mode): the card becomes a drag handle that moves the candidate between
  // stage columns. `isDragging` dims the source while it's in flight; a click still opens the drawer
  // (a native drag suppresses the click, so the two don't collide).
  editable?: boolean;
  isDragging?: boolean;
  onDragStart?: () => void;
  onDragEnd?: () => void;
}) {
  const tone = salaryTone(c.salaryExpectation, budgetMin, budgetMax);
  const exp = c.salaryExpectation;
  const hasBand = exp != null && budgetMin != null && budgetMax != null && budgetMax > budgetMin;
  const pct = hasBand ? Math.max(3, Math.min(100, ((exp - budgetMin) / (budgetMax - budgetMin)) * 100)) : 0;
  const stalled = c.status === "active" && c.daysInStage > STALL_DAYS;

  // Opens the candidate — as the right-side drawer when the caller wires `onOpenCandidate` (a button,
  // since it's an in-page panel), else as a link to the full profile page. Same card either way.
  const cardClass =
    "group block w-full rounded-lg border bg-card px-3.5 py-3 text-left shadow-sm outline-none transition-colors hover:bg-accent/60 focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring";
  const body = (
    <>
      {/* Identity — the card's headline. Name leads; current role · company is the qualifier beneath it. */}
      <div className="truncate text-sm font-semibold leading-snug group-hover:underline">{c.fullName}</div>
      <div className="mt-0.5 truncate text-xs text-muted-foreground">
        {c.currentTitle ?? "—"}
        {fields.employer && c.currentEmployer ? ` · ${c.currentEmployer}` : ""}
      </div>

      {/* Meta footer — status + momentum on one baseline. Both are quiet and small so they read as one
          calm row rather than two competing elements. */}
      <div className="mt-3 flex items-center justify-between gap-2">
        <StatusPill value={c.status} />
        {c.status === "active" ? (
          <span
            className={cn(
              "shrink-0 text-[11px] tabular-nums",
              stalled ? "text-[var(--color-warning)]" : "text-muted-foreground",
            )}
            title={`${c.daysInStage} days in this stage${stalled ? " — stalled" : ""}`}
          >
            {c.daysInStage}d
          </span>
        ) : null}
      </div>

      {fields.salary ? (
        <div className="mt-2 flex items-center gap-2">
          <span className={cn("shrink-0 text-xs tabular-nums", tone ? TONE_TEXT[tone] : "")}>
            {formatMoney(exp, c.currency)}
          </span>
          {hasBand ? (
            <div
              className="relative h-1 min-w-0 flex-1 rounded-full bg-foreground/10"
              title={`Budget ${formatMoney(budgetMin, budgetCurrency)}–${formatMoney(budgetMax, budgetCurrency)}`}
            >
              <span
                className={cn(
                  "absolute top-1/2 size-2 -translate-x-1/2 -translate-y-1/2 rounded-full ring-2 ring-[var(--color-card)]",
                  tone ? TONE_BG[tone] : "bg-foreground",
                )}
                style={{ left: `${pct}%` }}
              />
            </div>
          ) : null}
        </div>
      ) : null}
    </>
  );

  // Terminal candidates are read-only on the board — closed means closed, so the card isn't a drag handle
  // (matching the drawer, which blocks advancing/moving them). It still opens the drawer on click.
  const terminal = TERMINAL_STATUSES.has(c.status);
  const canDrag = editable && !terminal;
  const dragProps = canDrag
    ? {
        draggable: true,
        onDragStart: (e: DragEvent) => {
          e.dataTransfer.effectAllowed = "move";
          e.dataTransfer.setData("text/plain", c.linkId);
          onDragStart?.();
        },
        onDragEnd: () => onDragEnd?.(),
      }
    : {};
  const dragTitle = editable && terminal ? "Closed — reopen from the candidate drawer to move" : undefined;
  const cls = cn(cardClass, canDrag && "cursor-grab active:cursor-grabbing", isDragging && "opacity-40");

  if (onOpenCandidate) {
    return (
      <button
        type="button"
        onClick={() => onOpenCandidate(c.candidateId)}
        title={dragTitle}
        className={cls}
        {...dragProps}
      >
        {body}
      </button>
    );
  }
  return (
    <div className={cls} title={dragTitle} {...dragProps}>
      {body}
    </div>
  );
}

/** A stage with no applications, shown as a slim rail rather than a full empty column — keeps the
 *  whole funnel visible (and in order) while handing the horizontal room to staffed stages. Click
 *  to expand it back to a full column. */
function EmptyRail({
  stage,
  onExpand,
  isOver,
  dropProps,
}: {
  stage: StageDef;
  onExpand: () => void;
  isOver?: boolean;
  dropProps?: { onDragOver?: (e: DragEvent) => void; onDrop?: (e: DragEvent) => void };
}) {
  return (
    <button
      type="button"
      onClick={onExpand}
      {...dropProps}
      title={`${stage.label} — no applications. Click to expand.`}
      aria-label={`${stage.label}, no applications — expand column`}
      className={cn(
        "group flex w-11 shrink-0 flex-col items-center gap-2 self-stretch rounded-xl py-3 outline-none transition-colors focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring",
        isOver ? "bg-muted/80 ring-2 ring-inset ring-foreground/25" : "bg-muted/30 hover:bg-muted/60",
      )}
    >
      <span className="text-xs tabular-nums text-muted-foreground/50">0</span>
      <span className="eyebrow truncate [writing-mode:vertical-rl] rotate-180 text-muted-foreground/60 group-hover:text-muted-foreground">
        {stage.label}
      </span>
    </button>
  );
}

/** Pipeline board: one column per stage (same ladder as the tracker), each application sitting in
 *  its stage. Makes the funnel literal — where people cluster, where the gaps are. Scrolls
 *  horizontally so the full pipeline stays visible without squashing the cards. Empty stages
 *  collapse to slim rails (click to expand) so longer ladders stay scannable. */
export function CandidateKanban({
  candidates,
  ladder,
  searchId,
  budgetMin,
  budgetMax,
  budgetCurrency,
  fields = ALL_CARD_FIELDS,
  onOpenCandidate,
  onMove,
}: {
  candidates: CandidateCard[];
  ladder: StageDef[];
  searchId: string;
  budgetMin: number | null;
  budgetMax: number | null;
  budgetCurrency: string;
  fields?: CardFields;
  onOpenCandidate?: (candidateId: string) => void;
  // Admin edit mode only: when wired, cards drag between columns and dropping moves the candidate's
  // stage. Omitted in the client/preview view, which leaves the board a plain read-only funnel.
  onMove?: MoveFn;
}) {
  const editable = !!onMove;
  // The card currently in flight (its linkId + origin stage, to skip a no-op same-column drop) and the
  // column being hovered as a drop target (for the highlight).
  const [dragging, setDragging] = useState<{ linkId: string; stage: string } | null>(null);
  const [overStage, setOverStage] = useState<string | null>(null);
  const endDrag = useCallback(() => {
    setDragging(null);
    setOverStage(null);
  }, []);
  const dropOnStage = useCallback(
    (targetStage: string) => {
      if (dragging && dragging.stage !== targetStage) onMove?.(dragging.linkId, { stage: targetStage });
      endDrag();
    },
    [dragging, onMove, endDrag],
  );
  // Shared per-column drop wiring — preventDefault on dragover is what actually enables the drop.
  const dropPropsFor = (stageKey: string) =>
    editable
      ? {
          onDragOver: (e: DragEvent) => {
            e.preventDefault();
            e.dataTransfer.dropEffect = "move";
            setOverStage(stageKey);
          },
          onDrop: (e: DragEvent) => {
            e.preventDefault();
            dropOnStage(stageKey);
          },
        }
      : undefined;

  // Empty stages start collapsed; the user can expand any of them. Staffed stages are always full.
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const expand = (key: string) => setExpanded((prev) => new Set(prev).add(key));
  const collapse = (key: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      next.delete(key);
      return next;
    });

  // Edge-fade scroll affordance (audit K3): hint that columns continue beyond the visible edge.
  const scrollRef = useRef<HTMLDivElement>(null);
  const [edge, setEdge] = useState({ left: false, right: false });
  const recomputeEdges = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setEdge({ left: el.scrollLeft > 4, right: el.scrollLeft + el.clientWidth < el.scrollWidth - 4 });
  }, []);
  // biome-ignore lint/correctness/useExhaustiveDependencies: re-measure when the columns change too
  useEffect(() => {
    recomputeEdges();
    window.addEventListener("resize", recomputeEdges);
    return () => window.removeEventListener("resize", recomputeEdges);
  }, [recomputeEdges, candidates, expanded]);

  return (
    <div className="relative">
      <div ref={scrollRef} onScroll={recomputeEdges} className="overflow-x-auto pb-2">
        <div className="flex items-stretch gap-3">
          {ladder.map((stage) => {
            const items = candidates.filter((c) => c.stage === stage.key);
            const isOver = overStage === stage.key;
            if (items.length === 0 && !expanded.has(stage.key)) {
              return (
                <EmptyRail
                  key={stage.key}
                  stage={stage}
                  onExpand={() => expand(stage.key)}
                  isOver={isOver}
                  dropProps={dropPropsFor(stage.key)}
                />
              );
            }
            const empty = items.length === 0;
            return (
              <div
                key={stage.key}
                {...dropPropsFor(stage.key)}
                className={cn(
                  "flex w-56 shrink-0 flex-col rounded-xl transition-colors",
                  isOver ? "bg-muted/80 ring-2 ring-inset ring-foreground/25" : "bg-muted/40",
                )}
              >
                <div className="flex items-center justify-between gap-2 px-3 py-2.5">
                  <span className="eyebrow truncate">{stage.label}</span>
                  {empty ? (
                    <button
                      type="button"
                      onClick={() => collapse(stage.key)}
                      aria-label={`Collapse ${stage.label}`}
                      title="Collapse"
                      className="grid size-5 place-items-center rounded text-muted-foreground/50 outline-none transition-colors hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring"
                    >
                      <Plus className="size-3.5 rotate-45" />
                    </button>
                  ) : (
                    <span className="grid h-5 min-w-5 place-items-center rounded-full bg-foreground/10 px-1.5 text-[11px] font-medium tabular-nums text-foreground">
                      {items.length}
                    </span>
                  )}
                </div>
                <div className="space-y-2 px-2 pb-2">
                  {empty ? (
                    <div className="rounded-lg border border-dashed py-6 text-center text-xs text-muted-foreground/40">
                      —
                    </div>
                  ) : (
                    items.map((c) => (
                      <KanbanCard
                        key={c.linkId}
                        c={c}
                        searchId={searchId}
                        budgetMin={budgetMin}
                        budgetMax={budgetMax}
                        budgetCurrency={budgetCurrency}
                        fields={fields}
                        onOpenCandidate={onOpenCandidate}
                        editable={editable}
                        isDragging={dragging?.linkId === c.linkId}
                        onDragStart={() => setDragging({ linkId: c.linkId, stage: stage.key })}
                        onDragEnd={endDrag}
                      />
                    ))
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
      {edge.left ? (
        <div className="pointer-events-none absolute bottom-2 left-0 top-0 w-10 bg-gradient-to-r from-background to-transparent" />
      ) : null}
      {edge.right ? (
        <div className="pointer-events-none absolute bottom-2 right-0 top-0 w-10 bg-gradient-to-l from-background to-transparent" />
      ) : null}
    </div>
  );
}
